import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Shield, ChevronDown, ChevronRight } from 'lucide-react';

export default function ScorecardInfoPage() {
  const [config, setConfig] = useState(null);
  const [openPhase1, setOpenPhase1] = useState(true);
  const [openPhase2, setOpenPhase2] = useState(false);

  useEffect(() => {
    api.getScorecardConfig().then(res => setConfig(res.data)).catch(() => {});
  }, []);

  if (!config) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const renderCriteria = (criteria, phase) => (
    <div className="border border-border rounded-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground font-bold">
            <th className="text-left py-2.5 px-4 w-8">#</th>
            <th className="text-left py-2.5 px-4">Criterion</th>
            {phase === 'phase1' && <th className="text-center py-2.5 px-4 w-16">Weight</th>}
            <th className="text-center py-2.5 px-4 w-16">K.O.</th>
            <th className="text-left py-2.5 px-4">Scoring Options</th>
          </tr>
        </thead>
        <tbody>
          {criteria.map((c, i) => (
            <tr key={c.id} className="border-t border-border hover:bg-secondary/30 transition-colors duration-100">
              <td className="py-2.5 px-4 text-xs text-muted-foreground font-mono">{i + 1}</td>
              <td className="py-2.5 px-4 text-sm text-foreground font-medium">{c.name}</td>
              {phase === 'phase1' && (
                <td className="py-2.5 px-4 text-center font-mono text-xs text-muted-foreground">{c.weight}x</td>
              )}
              <td className="py-2.5 px-4 text-center">
                {c.is_ko ? (
                  <span className="text-red-400 text-xs font-mono">YES</span>
                ) : (
                  <span className="text-muted-foreground/30 text-xs">--</span>
                )}
              </td>
              <td className="py-2.5 px-4">
                <div className="space-y-0.5">
                  {c.options.map((o, oi) => (
                    <p key={`${o.score}-${o.label}`} className={`text-xs ${o.ko ? 'text-red-400 font-medium' : 'text-muted-foreground'}`}>
                      <span className="font-mono mr-1.5">{o.score}pts</span>
                      {o.label}
                      {o.ko && <span className="ml-1 text-red-500">[K.O.]</span>}
                    </p>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="scorecard-info-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">Scorecard Reference</h1>
        <p className="text-muted-foreground text-sm mt-1">Investment screening criteria and scoring methodology</p>
      </div>

      {/* Phase 1 */}
      <div className="border border-border rounded-sm bg-card">
        <button
          onClick={() => setOpenPhase1(!openPhase1)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/30 transition-colors"
          data-testid="toggle-phase1-info"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
            <span className="font-heading text-lg text-foreground">Phase 1 — K.O. Screen</span>
            <span className="text-xs text-muted-foreground ml-2">({config.phase1.length} criteria, max {config.phase1_max_score} pts)</span>
          </div>
          {openPhase1 ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </button>
        {openPhase1 && <div className="px-4 pb-4">{renderCriteria(config.phase1, 'phase1')}</div>}
      </div>

      {/* Phase 2 */}
      <div className="border border-border rounded-sm bg-card">
        <button
          onClick={() => setOpenPhase2(!openPhase2)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/30 transition-colors"
          data-testid="toggle-phase2-info"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-400" strokeWidth={1.5} />
            <span className="font-heading text-lg text-foreground">Phase 2 — Deep Dive</span>
            <span className="text-xs text-muted-foreground ml-2">({config.phase2.length} criteria, max {config.phase2_max_score} pts)</span>
          </div>
          {openPhase2 ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </button>
        {openPhase2 && <div className="px-4 pb-4">{renderCriteria(config.phase2, 'phase2')}</div>}
      </div>
    </div>
  );
}
